import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Mail, X, Copy, Check } from 'lucide-react';

const EMAIL = 'support@vivideo.ai';

export function HelpPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-surface rounded-2xl border border-glass-border shadow-xl max-w-sm w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary">{t('support.help')}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-light transition-colors">
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        </div>

        <p className="text-sm text-text-secondary mb-4">{t('support.helpMessage')}</p>

        <div className="flex items-center gap-2 p-3 rounded-xl bg-surface-light border border-glass-border">
          <Mail className="w-5 h-5 text-primary flex-shrink-0" />
          <a href={`mailto:${EMAIL}`} className="text-sm text-primary hover:underline flex-1 font-medium">
            {EMAIL}
          </a>
          <button
            onClick={handleCopy}
            className="p-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0"
            title={t('support.copyEmail')}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-text-tertiary" />
            )}
          </button>
        </div>
        {copied && (
          <p className="text-xs text-green-500 mt-2 text-center">{t('support.emailCopied')}</p>
        )}
      </div>
    </div>,
    document.body
  );
}
